/**
 * useGenerateVirtualRows - Batched virtual row generation to reduce whitespace
 *
 * Wraps useOptimizedVirtual and:
 * - Keeps a single consistent snapshot of { virtualItems, totalSize } so they never get out of sync
 * - Updates the snapshot in rAF on scroll (so we read virtualizer after it has updated) and in
 *   useLayoutEffect on data change, so virtualItems/totalSize stay in sync
 * - Avoids showing an empty window when the virtualizer briefly returns no items (keeps previous
 *   snapshot until a valid one is available)
 *
 * Use this instead of calling getVirtualItems()/getTotalSize() directly in the table body to
 * minimize whitespace during scroll and data updates.
 */

import type { VirtualItem } from '@tanstack/react-virtual';
import { useLayoutEffect, useRef, useState } from 'react';
import { useOptimizedVirtual } from './useOptimizedVirtual';
import type { VirtualRowItem, UseOptimizedVirtualOptions } from './useOptimizedVirtual';

export type UseGenerateVirtualRowsOptions<T> = UseOptimizedVirtualOptions<T>;

export type GenerateVirtualRowsSnapshot = {
  virtualItems: VirtualItem[];
  totalSize: number;
};

/**
 * Returns a stable snapshot of virtual items and total size, updated in a batched way to
 * minimize whitespace from virtualizer race conditions.
 */
export function useGenerateVirtualRows<T>(
  rows: VirtualRowItem<T>[],
  container: React.MutableRefObject<HTMLDivElement | null>,
  options: UseGenerateVirtualRowsOptions<T>
): GenerateVirtualRowsSnapshot {
  const { getVirtualItems, getTotalSize } = useOptimizedVirtual(rows, container, options);

  const rowsLengthRef = useRef(rows.length);

  const [snapshot, setSnapshot] = useState<GenerateVirtualRowsSnapshot>(() => ({
    virtualItems: getVirtualItems(),
    totalSize: getTotalSize(),
  }));

  const applySnapshot = (prev: GenerateVirtualRowsSnapshot) => {
    const virtualItems = getVirtualItems();
    const totalSize = getTotalSize();
    if (virtualItems.length === 0 && prev.virtualItems.length > 0 && rowsLengthRef.current > 0) {
      return prev;
    }
    return { virtualItems, totalSize };
  };

  const updateSnapshot = () => setSnapshot(prev => applySnapshot(prev));

  // Sync snapshot when row count, overscan, or getters change (data / options update)
  useLayoutEffect(() => {
    rowsLengthRef.current = rows.length;
    updateSnapshot();
  }, [rows.length, options.overscan, getVirtualItems, getTotalSize]);

  // On scroll: queue snapshot update to next frame so we read virtualizer after it has seen new scroll position
  useLayoutEffect(() => {
    const el = container.current;
    if (!el) return;

    let rafId = 0;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        updateSnapshot();
      });
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [container, getVirtualItems, getTotalSize]);

  return snapshot;
}
